import React, { useEffect, useState, useMemo } from 'react';
import { useWedding } from '../contexts/WeddingContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Edit, Trash, Phone, Mail, Globe, Instagram, Facebook, ChatBubble, Calendar, Download,
} from 'iconoir-react';

const EMPTY_FORM = {
  name: '', category: '',
  contact_name: '', phone: '', email: '',
  website: '', instagram: '', facebook: '', tiktok: '', whatsapp: '',
  event_ids: [],
  notes: '',
};

export default function VendorsPage() {
  const {
    currentWedding, vendors, categories, events,
    fetchVendors, createVendor, updateVendor, deleteVendor, fetchCategories, fetchEvents,
  } = useWedding();

  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    if (currentWedding) {
      fetchVendors(currentWedding.id);
      fetchCategories();
      fetchEvents(currentWedding.id);
    }
  }, [currentWedding, fetchVendors, fetchCategories, fetchEvents]);

  const eventNameById = useMemo(() => {
    const m = {};
    (events || []).forEach((e) => { m[e.id] = e.name; });
    return m;
  }, [events]);

  const openModal = (vendor = null) => {
    if (vendor) {
      setEditingVendor(vendor);
      setFormData({
        name: vendor.name || '',
        category: vendor.category || '',
        contact_name: vendor.contact_name || '',
        phone: vendor.phone || '',
        email: vendor.email || '',
        website: vendor.website || '',
        instagram: vendor.instagram || '',
        facebook: vendor.facebook || '',
        tiktok: vendor.tiktok || '',
        whatsapp: vendor.whatsapp || '',
        event_ids: vendor.event_ids || [],
        notes: vendor.notes || '',
      });
    } else {
      setEditingVendor(null);
      setFormData(EMPTY_FORM);
    }
    setShowModal(true);
  };

  const toggleEvent = (eventId) => {
    setFormData((prev) => ({
      ...prev,
      event_ids: prev.event_ids.includes(eventId)
        ? prev.event_ids.filter((id) => id !== eventId)
        : [...prev.event_ids, eventId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { wedding_id: currentWedding.id, ...formData };
    if (editingVendor) await updateVendor(editingVendor.id, payload);
    else await createVendor(payload);
    setShowModal(false);
    fetchVendors(currentWedding.id);
  };

  const handleDelete = async (vendorId) => {
    if (window.confirm('Delete this vendor? Their payments will stay on the Payments page.')) {
      await deleteVendor(vendorId);
    }
  };

  if (!currentWedding) {
    return (
      <Layout>
        <div className="text-center py-12 text-muted">Please select or create a wedding first.</div>
      </Layout>
    );
  }

  const vendorsByCategory = vendors.reduce((acc, v) => {
    const cat = v.category || 'Other';
    acc[cat] = acc[cat] || [];
    acc[cat].push(v);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="space-y-8" data-testid="vendors-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight">
              Vendors
            </h1>
            <p className="text-muted font-light mt-2">
              Your wedding contact book — phone, web, socials, and the events each vendor covers.
              <span className="block text-xs mt-1">Track money on the <span className="text-terracotta">Payments</span> tab.</span>
            </p>
          </div>
          <Button onClick={() => openModal()} className="btn-primary flex items-center gap-2" data-testid="add-vendor-btn">
            <Plus className="w-4 h-4" /> Add Vendor
          </Button>
        </div>

        {/* Day-of contacts PDF download — discoverable from this page too */}
        {vendors.length > 0 && (
          <div className="flex justify-end">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/contact-sheet/${currentWedding.id}/pdf`}
              className="btn-secondary flex items-center gap-1.5 no-underline text-xs px-3 py-2"
              data-testid="vendors-download-contact-sheet-btn"
            >
              <Download className="w-3.5 h-3.5" /> Download Day-of Contact Sheet PDF
            </a>
          </div>
        )}

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Total Vendors</p>
            <p className="font-mono text-2xl text-charcoal tracking-tight" data-testid="total-vendors">
              {vendors.length}
            </p>
          </Card>
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Categories</p>
            <p className="font-mono text-2xl text-charcoal tracking-tight">
              {Object.keys(vendorsByCategory).length}
            </p>
          </Card>
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Events Covered</p>
            <p className="font-mono text-2xl text-charcoal tracking-tight">
              {new Set(vendors.flatMap((v) => v.event_ids || [])).size}
            </p>
          </Card>
        </div>

        {/* Vendors */}
        {vendors.length === 0 ? (
          <Card className="card-surface p-12 text-center">
            <p className="text-muted mb-4">No vendors added yet</p>
            <Button onClick={() => openModal()} className="btn-primary" data-testid="create-first-vendor-btn">
              Add Your First Vendor
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vendors.map((vendor) => (
              <VendorCard
                key={vendor.id}
                vendor={vendor}
                eventNameById={eventNameById}
                onEdit={() => openModal(vendor)}
                onDelete={() => handleDelete(vendor.id)}
              />
            ))}
          </div>
        )}

        {/* Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {editingVendor ? 'Edit Vendor' : 'Add Vendor'}
              </DialogTitle>
              <DialogDescription className="text-muted">
                All contact info in one place. Payments are tracked separately on the Payments tab.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              {/* Name + Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Vendor Name</Label>
                  <Input value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-field h-12" placeholder="e.g., Meera's Mehndi"
                    required data-testid="vendor-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Category</Label>
                  <Select value={formData.category}
                    onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger className="input-field h-12" data-testid="vendor-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Contact Person</Label>
                <Input value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  className="input-field h-12" placeholder="Who to ask for"
                  data-testid="vendor-contact-input" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Phone</Label>
                  <Input value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-field h-12" placeholder="+1 555 …"
                    data-testid="vendor-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Email</Label>
                  <Input type="email" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field h-12" placeholder="hello@vendor.com"
                    data-testid="vendor-email-input" />
                </div>
              </div>

              {/* Web + socials */}
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Website</Label>
                <Input value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="input-field h-12" placeholder="https://…"
                  data-testid="vendor-website-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider flex items-center gap-2">
                    <Instagram className="w-3.5 h-3.5" /> Instagram
                  </Label>
                  <Input value={formData.instagram}
                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    className="input-field h-12" placeholder="@handle or link"
                    data-testid="vendor-instagram-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider flex items-center gap-2">
                    <Facebook className="w-3.5 h-3.5" /> Facebook
                  </Label>
                  <Input value={formData.facebook}
                    onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                    className="input-field h-12" placeholder="Page name or link"
                    data-testid="vendor-facebook-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">TikTok</Label>
                  <Input value={formData.tiktok}
                    onChange={(e) => setFormData({ ...formData, tiktok: e.target.value })}
                    className="input-field h-12" placeholder="@handle or link"
                    data-testid="vendor-tiktok-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider flex items-center gap-2">
                    <ChatBubble className="w-3.5 h-3.5" /> WhatsApp
                  </Label>
                  <Input value={formData.whatsapp}
                    onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    className="input-field h-12" placeholder="Phone or link"
                    data-testid="vendor-whatsapp-input" />
                </div>
              </div>

              {/* Events */}
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" /> Events they're part of
                </Label>
                {(!events || events.length === 0) ? (
                  <p className="text-xs text-muted italic">Add events to your wedding first to link vendors.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 p-3 border border-border rounded-sm bg-page/30">
                    {events.map((evt) => (
                      <label key={evt.id}
                        className="flex items-center gap-2 cursor-pointer py-1 hover:text-charcoal transition-colors"
                        data-testid={`vendor-event-option-${evt.id}`}>
                        <Checkbox
                          checked={formData.event_ids.includes(evt.id)}
                          onCheckedChange={() => toggleEvent(evt.id)}
                        />
                        <span className="text-sm text-charcoal truncate">{evt.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Notes</Label>
                <Input value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-field h-12" placeholder="Anything else worth remembering"
                  data-testid="vendor-notes-input" />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary flex-1" data-testid="save-vendor-btn">
                  {editingVendor ? 'Update' : 'Add vendor'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

function VendorCard({ vendor, eventNameById, onEdit, onDelete }) {
  const eventNames = (vendor.event_ids || [])
    .map((id) => eventNameById[id])
    .filter(Boolean);
  const socials = [
    { key: 'instagram', icon: Instagram, href: linkForHandle(vendor.instagram, 'instagram.com') },
    { key: 'facebook',  icon: Facebook,  href: linkForHandle(vendor.facebook,  'facebook.com') },
    { key: 'tiktok',    icon: null,      label: 'TikTok', href: linkForHandle(vendor.tiktok, 'tiktok.com/@') },
    { key: 'whatsapp',  icon: ChatBubble,href: whatsappLink(vendor.whatsapp) },
  ].filter((s) => s.href);

  return (
    <Card className="card-surface p-6 flex flex-col gap-4" data-testid={`vendor-card-${vendor.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-serif text-xl text-charcoal truncate">{vendor.name}</h3>
          <p className="text-sm text-terracotta">{vendor.category}</p>
          {vendor.contact_name && (
            <p className="text-sm text-charcoal mt-1">{vendor.contact_name}</p>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onEdit} className="p-2 text-muted hover:text-charcoal transition-colors"
            data-testid={`edit-vendor-${vendor.id}`}>
            <Edit className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button onClick={onDelete} className="p-2 text-muted hover:text-terracotta transition-colors"
            data-testid={`delete-vendor-${vendor.id}`}>
            <Trash className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Primary contact lines */}
      <div className="space-y-1">
        {vendor.phone && (
          <a href={`tel:${vendor.phone}`}
            className="flex items-center gap-2 text-sm text-muted hover:text-charcoal transition-colors">
            <Phone className="w-3 h-3" /> {vendor.phone}
          </a>
        )}
        {vendor.email && (
          <a href={`mailto:${vendor.email}`}
            className="flex items-center gap-2 text-sm text-muted hover:text-charcoal transition-colors truncate">
            <Mail className="w-3 h-3" /> {vendor.email}
          </a>
        )}
        {vendor.website && (
          <a href={normalizeUrl(vendor.website)} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-muted hover:text-charcoal transition-colors">
            <Globe className="w-3 h-3" /> Website
          </a>
        )}
      </div>

      {/* Socials row */}
      {socials.length > 0 && (
        <div className="flex items-center gap-2">
          {socials.map((s) => (
            <a key={s.key} href={s.href} target="_blank" rel="noopener noreferrer"
              className="p-1.5 border border-border rounded-sm text-muted hover:text-charcoal hover:border-charcoal transition-colors"
              aria-label={s.key}
              data-testid={`vendor-social-${s.key}-${vendor.id}`}>
              {s.icon ? <s.icon className="w-3.5 h-3.5" /> : <span className="text-[10px] font-mono uppercase px-0.5">{s.label}</span>}
            </a>
          ))}
        </div>
      )}

      {/* Events */}
      {eventNames.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border">
          {eventNames.map((n) => (
            <Badge key={n} className="bg-ochre/10 text-ochre border border-ochre/30 text-xs font-normal">
              {n}
            </Badge>
          ))}
        </div>
      )}

      {vendor.notes && (
        <p className="text-xs text-muted italic pt-2 border-t border-border">{vendor.notes}</p>
      )}
    </Card>
  );
}

function normalizeUrl(u) {
  if (!u) return '';
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
}

function linkForHandle(value, domain) {
  if (!value) return '';
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const handle = trimmed.replace(/^@/, '');
  if (domain.endsWith('@')) return `https://${domain}${handle}`;
  return `https://${domain}/${handle}`;
}

function whatsappLink(value) {
  if (!value) return '';
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '';
}
